import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  PlaylistAdd as SeedIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import PageWithHeader from '../common/PageWithHeader';
import LoadingCover from '../common/LoadingCover';
import { useRouter } from '../../hooks/useRouter';
import {
  useGetBusinessUnitsQuery,
  useCreateBusinessUnitMutation,
  useUpdateBusinessUnitMutation,
  useDeleteBusinessUnitMutation,
  useSeedBusinessUnitsMutation,
  BusinessUnit,
} from '../../redux/businessUnitApi';

// Business Unit form interface
interface BusinessUnitFormData {
  name: string;
  description: string;
  sortOrder: number;
  isActive: boolean;
}

const initialFormData: BusinessUnitFormData = {
  name: '',
  description: '',
  sortOrder: 0,
  isActive: true,
};

const BusinessUnitManagement: React.FC = () => {
  const router = useRouter();
  const { data: businessUnits, isLoading, refetch } = useGetBusinessUnitsQuery();
  const [createBusinessUnit, { isLoading: isCreateLoading }] = useCreateBusinessUnitMutation();
  const [updateBusinessUnit, { isLoading: isUpdateLoading }] = useUpdateBusinessUnitMutation();
  const [deleteBusinessUnit, { isLoading: isDeleteLoading }] = useDeleteBusinessUnitMutation();
  const [seedBusinessUnits, { isLoading: isSeedLoading }] = useSeedBusinessUnitsMutation();

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<'name' | 'sortOrder'>('sortOrder');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<BusinessUnit | null>(null);
  const [formData, setFormData] = useState<BusinessUnitFormData>(initialFormData);
  const [formError, setFormError] = useState<string>('');
  const [seedResult, setSeedResult] = useState<{ created: number; skipped: number } | null>(null);

  // Filter and sort business units
  const filteredUnits = useMemo(() => {
    if (!businessUnits) return [];
    
    return [...businessUnits]
      .filter(unit => {
        // Search filter
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = !searchTerm || 
          unit.name.toLowerCase().includes(searchLower) ||
          unit.description?.toLowerCase().includes(searchLower);
        
        // Status filter
        const matchesStatus = statusFilter === 'all' || 
          (statusFilter === 'active' && unit.isActive) ||
          (statusFilter === 'inactive' && !unit.isActive);
        
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        if (sortField === 'sortOrder') {
          const diff = (a.sortOrder || 0) - (b.sortOrder || 0);
          return sortOrder === 'asc' ? diff : -diff;
        }
        const aVal = (a.name || '').toLowerCase();
        const bVal = (b.name || '').toLowerCase();
        if (sortOrder === 'asc') {
          return aVal.localeCompare(bVal);
        }
        return bVal.localeCompare(aVal);
      });
  }, [businessUnits, searchTerm, statusFilter, sortField, sortOrder]);

  // Handle sort
  const handleSort = (field: 'name' | 'sortOrder') => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Handle create
  const handleOpenCreate = () => {
    const maxSortOrder = businessUnits?.reduce((max, u) => Math.max(max, u.sortOrder || 0), 0) || 0;
    setFormData({ ...initialFormData, sortOrder: maxSortOrder + 1 });
    setFormError('');
    setShowCreateDialog(true);
  };

  const handleCreateUnit = async () => {
    setFormError('');
    
    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }

    // Check for duplicate name
    const exists = businessUnits?.some(u => u.name.toLowerCase() === formData.name.toLowerCase());
    if (exists) {
      setFormError('A business unit with this name already exists');
      return;
    }

    try {
      await createBusinessUnit({
        name: formData.name,
        description: formData.description || undefined,
        sortOrder: formData.sortOrder,
        isActive: formData.isActive,
      }).unwrap();
      setShowCreateDialog(false);
      refetch();
    } catch (error: any) {
      setFormError(error?.data?.error?.message || 'Failed to create business unit');
    }
  };

  // Handle edit
  const handleOpenEdit = (unit: BusinessUnit) => {
    setSelectedUnit(unit);
    setFormData({
      name: unit.name,
      description: unit.description || '',
      sortOrder: unit.sortOrder || 0,
      isActive: unit.isActive,
    });
    setFormError('');
    setShowEditDialog(true);
  };

  const handleUpdateUnit = async () => {
    if (!selectedUnit) return;
    setFormError('');

    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }

    // Check for duplicate name (excluding current unit)
    const exists = businessUnits?.some(
      u => u.id !== selectedUnit.id && u.name.toLowerCase() === formData.name.toLowerCase()
    );
    if (exists) {
      setFormError('A business unit with this name already exists');
      return;
    }

    try {
      await updateBusinessUnit({
        id: selectedUnit.id!,
        body: {
          name: formData.name,
          description: formData.description || undefined,
          sortOrder: formData.sortOrder,
          isActive: formData.isActive,
        },
      }).unwrap();
      setShowEditDialog(false);
      refetch();
    } catch (error: any) {
      setFormError(error?.data?.error?.message || 'Failed to update business unit');
    }
  };

  // Handle delete
  const handleOpenDelete = (unit: BusinessUnit) => {
    setSelectedUnit(unit);
    setShowDeleteDialog(true);
  };

  const handleDeleteUnit = async () => {
    if (!selectedUnit) return;
    
    try {
      await deleteBusinessUnit({ id: selectedUnit.id! }).unwrap();
      setShowDeleteDialog(false);
      refetch();
    } catch (error: any) {
      alert(error?.data?.error?.message || 'Failed to delete business unit');
    }
  };

  // Handle seed defaults
  const handleSeedDefaults = async () => {
    try {
      const result = await seedBusinessUnits().unwrap();
      setSeedResult(result);
      refetch();
    } catch (error: any) {
      alert(error?.data?.error?.message || 'Failed to seed business units');
    }
  };

  if (isLoading) return <LoadingCover />;

  return (
    <PageWithHeader>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => router.push('/')}>
                <ArrowBackIcon />
              </IconButton>
              <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Business Units
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage business units across the organization
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<PeopleIcon />}
                onClick={() => router.push('/admin/users')}
              >
                User Management
              </Button>
              <Button
                variant="outlined"
                startIcon={<SeedIcon />}
                onClick={handleSeedDefaults}
                disabled={isSeedLoading}
              >
                {isSeedLoading ? <CircularProgress size={20} /> : 'Seed Defaults'}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreate}
              >
                Add Business Unit
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Seed Result Alert */}
        {seedResult && (
          <Alert 
            severity="success" 
            sx={{ mb: 2 }}
            onClose={() => setSeedResult(null)}
          >
            Seeded {seedResult.created} new business units. {seedResult.skipped} already existed.
          </Alert>
        )}

        {/* Stats */}
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {businessUnits?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Business Units
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {businessUnits?.filter(u => u.isActive).length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="text.secondary">
                {businessUnits?.filter(u => !u.isActive).length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Inactive
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              placeholder="Search business units..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            <Stack direction="row" spacing={1}>
              <Chip
                label="All"
                variant={statusFilter === 'all' ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter('all')}
                color={statusFilter === 'all' ? 'primary' : 'default'}
              />
              <Chip
                label="Active"
                variant={statusFilter === 'active' ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter('active')}
                color={statusFilter === 'active' ? 'success' : 'default'}
              />
              <Chip
                label="Inactive"
                variant={statusFilter === 'inactive' ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter('inactive')}
                color={statusFilter === 'inactive' ? 'default' : 'default'}
              />
            </Stack>
            <Box sx={{ flex: 1 }} />
            <IconButton onClick={() => refetch()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Paper>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell width={60}>
                  <TableSortLabel
                    active={sortField === 'sortOrder'}
                    direction={sortField === 'sortOrder' ? sortOrder : 'asc'}
                    onClick={() => handleSort('sortOrder')}
                  >
                    #
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'name'}
                    direction={sortField === 'name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Description</TableCell>
                <TableCell width={100}>Status</TableCell>
                <TableCell width={120} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUnits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {businessUnits?.length === 0
                        ? 'No business units found. Click "Seed Defaults" to add initial values.'
                        : 'No matching business units found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUnits.map((unit) => (
                  <TableRow key={unit.id} hover>
                    <TableCell>{unit.sortOrder}</TableCell>
                    <TableCell>
                      <Typography fontWeight="medium">{unit.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {unit.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={unit.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={unit.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEdit(unit)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDelete(unit)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Business Unit</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
              <TextField
                label="Sort Order"
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreateUnit}
              disabled={isCreateLoading}
            >
              {isCreateLoading ? <CircularProgress size={20} /> : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Business Unit</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
              <TextField
                label="Sort Order"
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleUpdateUnit}
              disabled={isUpdateLoading}
            >
              {isUpdateLoading ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
          <DialogTitle>Delete Business Unit</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete <strong>{selectedUnit?.name}</strong>?
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              This action cannot be undone. Consider deactivating the business unit instead.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteUnit}
              disabled={isDeleteLoading}
            >
              {isDeleteLoading ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageWithHeader>
  );
};

export default BusinessUnitManagement;
